import HistoryIcon from '@mui/icons-material/History';
import { CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FC, Key, useState } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SaveIcon from '@mui/icons-material/Save';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';

import { Application } from '@features/applications';
import { white } from '@styles/colors';
import { useGetApplicationHistory } from '@features/applications/hooks/useGetApplicationHistory';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
type ApplicationHistoryTabProps = {
  application: Application;
};
type ApplicationHistoryItemColors = 'success' | 'error' | 'info' | 'primary';

type ApplicationHistoryItemColor =
  | 'success'
  | 'primaryPurple'
  | 'error'
  | 'info'
  | 'primary'
  | 'secondary'
  | 'action'
  | 'disabled'
  | 'inherit'
  | 'lightPurple';

type ApplicationHistoryItemContent = {
  color: ApplicationHistoryItemColor;
  icon: React.ElementType;
  message: string;
};
type ApplicationHistoryItem = {
  timestamp: string;
  color: ApplicationHistoryItemColor;
  message: string;
};
const ApplicationHistoryTab: FC<ApplicationHistoryTabProps> = ({
  application,
}) => {
  const [errorText, setErrorText] = useState<string>('');
  const { applicationHistory, isLoadingHistory } = useGetApplicationHistory({
    setErrorText,
    id: application.id,
  });

  const gatHistoryContent = (
    content: string,
  ): ApplicationHistoryItemContent => {
    if (content.includes('saved')) {
      return { color: 'success', icon: SaveIcon, message: content };
    }
    if (content.includes('declined')) {
      return { color: 'error', icon: CloseIcon, message: content };
    }
    if (content.includes('Job offer accepted') || content.includes('hired')) {
      return { color: 'primaryPurple', icon: CheckIcon, message: content };
    }
    if (content.includes('Job offer')) {
      return { color: 'lightPurple', icon: CheckIcon, message: content };
    }
    if (content.includes('Interview deleted')) {
      return { color: 'error', icon: CalendarMonthIcon, message: content };
    }
    if (content.includes('Direct Interview') || content.includes('Interview invite sent')) {
      return { color: 'secondary', icon: CalendarMonthIcon, message: content };
    }
    if (content.includes('Open interview')) {
      return {
        color: 'lightPurple',
        icon: QuestionAnswerIcon,
        message: content,
      };
    }
    if (content.includes('Interview block')) {
      return { color: 'success', icon: PersonAddIcon, message: content };
    }
    if (
      content.includes('Interview invite accepted') ||
      content.includes('Interview invite seen')
    ) {
      return { color: 'success', icon: CalendarMonthIcon, message: content };
    }
    if (content.includes('archived')) {
      return { color: 'info', icon: ArchiveIcon, message: content };
    }
    if (content.includes('Message')) {
      return { color: 'primary', icon: MessageIcon, message: content };
    }
    if (content.includes('Onboarding')) {
      return {
        color: 'success',
        icon: ThumbUpAltOutlinedIcon,
        message: content,
      };
    }
    return { color: 'success', icon: HistoryIcon, message: content };
  };

  const getApplicationHistoryItems = () => {
    const events = [
      {
        message: `Application for ${application.position} submitted`,
        timestamp: application.dt_timestamp,
        color: 'success',
      },
      {
        message: `Application for ${application.position} withdrawn`,
        timestamp: application.withdrawn,
        color: 'error',
      },
      {
        message: `Application for ${application.position} saved`,
        timestamp: application.saved,
        color: 'success',
      },
      {
        message: `Application for ${application.position} declined`,
        timestamp: application.declined,
        color: 'error',
      },
      {
        message: `Application for ${application.position} archived`,
        timestamp: application.employer_archive_date,
        color: 'info',
      },
      {
        message: 'Message sent',
        timestamp: application.messaged,
        color: 'primary',
      },
      {
        message: `Interview request for ${application.position} sent`,
        timestamp: application.interview_requested,
        color: 'success',
      },
      {
        message: `Interview request for ${application.position} accepted`,
        timestamp: application.interview_accepted,
        color: 'success',
      },
      {
        message: `Interview request for ${application.position} declined`,
        timestamp: application.interview_declined,
        color: 'error',
      },
      {
        message: `Interview request for ${application.position} deleted`,
        timestamp: application.interview_deleted,
        color: 'error',
      },
      {
        message: `Job offer for ${application.position} sent`,
        timestamp: application.job_offered,
        color: 'success',
      },
      {
        message: `Job offer for ${application.position} accepted`,
        timestamp:
          application.job_offer_response === 'ACCEPT'
            ? application.job_offer_response_d
            : null,
        color: 'success',
      },
      {
        message: `Job offer for ${application.position} declined`,
        timestamp:
          application.job_offer_response === 'DECLINE'
            ? application.job_offer_response_d
            : null,
        color: 'error',
      },
      {
        message: 'Onboarding complete',
        timestamp: application.onboarding_complete,
        color: 'success',
      },
    ];

    const formattedEvents: ApplicationHistoryItem[] = events
      .filter((event) => Boolean(event.timestamp))
      .map((event) => ({
        ...event,
        color: event.color as ApplicationHistoryItemColors,
        timestamp: event.timestamp as string,
      }))
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )!;

    return formattedEvents;
  };
  return (
    <Stack bgcolor={white} p={4} borderRadius={8} height="100%" spacing={2}>
      {isLoadingHistory ? (
        <CircularProgress size={50} sx={{ alignSelf: 'center', mt: 2 }} />
      ) : applicationHistory?.object?.length > 0 ? (
        applicationHistory.object.map(
          (
            historyItem: { content: string; date_created: string },
            index: number,
          ) => {
            const {
              icon: Icon,
              color,
              message,
            } = gatHistoryContent(historyItem.content);

            return (
              <Typography
                key={index}
                display="flex"
                alignItems="center"
                fontSize="14px"
                color="text.primary"
              >
                <Icon
                  color={color}
                  sx={{ mr: 1, mt: '2px' }}
                  style={{ fontSize: '20px' }}
                />
                {message}
                {'\u00A0'}for{'\u00A0'}
                <b>{application.job_listing.s_title}</b>
                {'\u00A0'}—{'\u00A0'}
                {dayjs(historyItem.date_created).format('h:mma MM/DD/YY')}
              </Typography>
            );
          },
        )
      ) : (
        getApplicationHistoryItems().map(
          (
            { color, message, timestamp }: ApplicationHistoryItem,
            index: number,
          ) => (
            <Typography
              key={index}
              display="flex"
              alignItems="center"
              fontSize="14px"
            >
              <HistoryIcon
                //@ts-ignore
                color={color}
                sx={{ mr: 1 }}
                style={{ fontSize: '20px' }}
              />
              {`${message} ${dayjs(timestamp).format('h:mma MM/DD/YY')}`}
            </Typography>
          ),
        )
      )}
    </Stack>
  );
};

export default ApplicationHistoryTab;
